/* eslint-disable @typescript-eslint/naming-convention */
// types
import { MARKETPLACE_BUSINESS_LINE, MARKETPLACE_CATEGORY, MARKETPLACE_ID } from "@swiggy-private/cart-types";
import { MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";
import { CartActionTypes as MROCartActionTypes } from "@cart-client/reducers/CartReducer";
import { MROCartAction, MROCartState } from "@cart-client/reducers/MROCartReducer";
// utils
import { getBridgeSdk, isQuickCheckoutFlow } from "@swiggy-private/checkout-core";
import { getMROCartType, validateAllSubcartsSelectedInMROCart } from "@cart-client/helpers/MROFoodCart";

/**
 * Options for the Quick Checkout cart reducer.
 */
export interface QuickCheckoutCartReducerOptions {
    /**
     * Reads whether the native bridge write must be suppressed. Quick Checkout renders a compact
     * sheet that is not the source of truth for the app's local cart, so the write is skipped
     * while that screen is active and resumed once the user opens the full MRO cart through the
     * View Cart CTA.
     *
     * This is a getter rather than a plain boolean so a single reducer instance can serve both
     * screens and its identity never has to change. React 16 computes the next state eagerly in
     * `dispatchAction` and tags the update with the reducer that produced it; if the identity
     * differs by the time that update is processed, the reducer runs a second time and this side
     * effect would fire again using the post-navigation value.
     */
    shouldSkipNativeLocalCartSync?: () => boolean;
}

/**
 * Builds the reducer used by the Quick Checkout cart flow.
 *
 * Behaviour matches `QCCartReducer` and it shares the same `QCCartState` / `QCCartAction`
 * contract, with the single deviation that the native `updateLocalCart` bridge write can be
 * suppressed per action. Kept as a separate reducer so the QC cart flow,
 * which enters with `cartSource === QUICK_CHECKOUT`, stays on the untouched `QCCartReducer`.
 */
export function createQuickCheckoutCartReducer(
    options: QuickCheckoutCartReducerOptions = {},
): (state: MROCartState, action: MROCartAction) => MROCartState {
    return function QuickCheckoutCartReducer(state: MROCartState, action: MROCartAction): MROCartState {
        const { localCart, reviewedCart } = state || {};
        const { type, payload } = action || {};
        const shouldSyncLocalCartToNative = !options.shouldSkipNativeLocalCartSync?.();

        switch (type) {
            case MROCartActionTypes.UPDATE_LOCAL_CART: {
                // Update only the local Quick Checkout cart and sync with bridge
                const updatedCart = {
                    ...state,
                    localCart: { ...localCart, ...payload },
                };
                if (shouldSyncLocalCartToNative) {
                    getBridgeSdk().updateLocalCart({
                        tenant: {
                            marketplaceBusinessline: MARKETPLACE_BUSINESS_LINE.REGULAR,
                            marketplaceCategory: MARKETPLACE_CATEGORY.FOOD,
                            marketplaceId: MARKETPLACE_ID.SWIGGY,
                            cartType: getMROCartType(),
                        },
                        data: updatedCart.localCart,
                    });
                }
                return updatedCart;
            }
            case MROCartActionTypes.UPDATE_REVIEWED_CART:
                // Update only the reviewed Quick Checkout cart
                return Object.assign({}, state, { reviewedCart: payload });
            case MROCartActionTypes.UPDATE_CART: {
                if (shouldSyncLocalCartToNative) {
                    const prefetchEnabled =
                        typeof window !== "undefined" && window.__headers__?.cartpageprefetch === "true";
                    const allSubcartsSelected = validateAllSubcartsSelectedInMROCart(
                        payload.reviewedCart,
                        payload.localCart,
                    );
                    const prefetchBackSyncEnabled = prefetchEnabled && allSubcartsSelected;
                    const partialPrefetchBackSyncEnabled =
                        prefetchEnabled && !allSubcartsSelected && !isQuickCheckoutFlow();
                    const enabledBackSyncForPartialPrefetch = prefetchBackSyncEnabled || partialPrefetchBackSyncEnabled;

                    let cartResponse: { last_updated_timestamp: string; response: Record<string, unknown> } | undefined;
                    if (enabledBackSyncForPartialPrefetch) {
                        // Use original prefetch timestamp for initial load (preserves TTL clock),
                        // Date.now() for real API responses (background refresh, mutations)
                        const prefetchTs = window.__prefetchTimestamp__;
                        const timestamp = prefetchTs || String(Date.now());
                        // Clear after first use — subsequent UPDATE_CART dispatches use Date.now()
                        if (prefetchTs) {
                            window.__prefetchTimestamp__ = undefined;
                        }

                        let response = payload.reviewedCart as unknown as Record<string, unknown>;
                        if (partialPrefetchBackSyncEnabled) {
                            response = {};
                        }

                        cartResponse = {
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            last_updated_timestamp: timestamp,
                            response,
                        };
                    }
                    // Update both local and reviewed MRO cart, and sync with bridge
                    getBridgeSdk().updateLocalCart({
                        tenant: {
                            marketplaceBusinessline: MARKETPLACE_BUSINESS_LINE.REGULAR,
                            marketplaceCategory: MARKETPLACE_CATEGORY.FOOD,
                            marketplaceId: MARKETPLACE_ID.SWIGGY,
                            cartType: getMROCartType(),
                        },
                        data: {
                            ...(state.localCart || {}),
                            ...(payload?.localCart || {}),
                        },
                        ...(enabledBackSyncForPartialPrefetch && { cart_response: cartResponse }),
                    });
                }

                return {
                    ...state,
                    ...payload,
                };
            }
            case MROCartActionTypes.CLEAR_LOCAL_CART: {
                // Clear the local Quick Checkout cart.
                const incomingReviewedCart = (payload as { reviewedCart: MROReviewedCart }).reviewedCart;
                return {
                    ...state,
                    localCart: null,
                    reviewedCart: {
                        ...reviewedCart,
                        ...(incomingReviewedCart || {}),
                    },
                };
            }
            default:
                throw new Error(`unhandled Quick Checkout cart action type ${type}`);
        }
    };
}
